/**
 * Vehicles API Client
 * Epic 34: Járműnyilvántartás (ADR-027)
 */

import { api } from './client';

// ============================================
// TYPES
// ============================================

export type RentalVehicleType = 'trailer' | 'aggregator' | 'platform' | 'other';
export type CompanyVehicleType = 'car' | 'van' | 'truck';
export type VehicleType = RentalVehicleType | CompanyVehicleType;
export type RentalVehicleStatus = 'active' | 'rented' | 'maintenance' | 'inactive';
export type CompanyVehicleStatus = 'active' | 'in_use' | 'maintenance' | 'inactive';
export type VehicleStatus = RentalVehicleStatus | CompanyVehicleStatus;
export type FuelType = 'petrol' | 'diesel' | 'electric' | 'hybrid';

export interface RentalVehicle {
  id: string;
  vehicleType: 'RENTAL';
  licensePlate: string;
  type: RentalVehicleType;
  name: string;
  brand: string;
  model: string;
  status: RentalVehicleStatus;
  technicalExpiryDate: string;
  registrationExpiryDate: string;
  insuranceExpiryDate: string;
  currentRentalId?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CompanyVehicle {
  id: string;
  vehicleType: 'COMPANY';
  licensePlate: string;
  type: CompanyVehicleType;
  name: string;
  brand: string;
  model: string;
  year: number;
  status: CompanyVehicleStatus;
  assignedTo?: string;
  assignedTenantId?: string;
  assignedTenantName?: string;
  technicalExpiryDate: string;
  registrationExpiryDate: string;
  kgfbExpiryDate: string;
  cascoExpiryDate?: string;
  highwayPassExpiryDate?: string;
  fuelType: FuelType;
  mileage: number;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export type Vehicle = RentalVehicle | CompanyVehicle;

export interface VehicleListResponse<T = Vehicle> {
  data: T[];
  meta: {
    total: number;
    page: number;
    pageSize: number;
    hasMore: boolean;
  };
}

export interface VehicleResponse<T = Vehicle> {
  data: T;
}

export interface VehicleFilters {
  vehicleType?: 'RENTAL' | 'COMPANY';
  type?: string;
  status?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface CreateRentalVehicleData {
  licensePlate: string;
  type: RentalVehicleType;
  name: string;
  brand: string;
  model: string;
  status?: RentalVehicleStatus;
  technicalExpiryDate: string;
  registrationExpiryDate: string;
  insuranceExpiryDate: string;
  notes?: string;
}

export interface CreateCompanyVehicleData {
  licensePlate: string;
  type: CompanyVehicleType;
  name: string;
  brand: string;
  model: string;
  year: number;
  status?: CompanyVehicleStatus;
  fuelType: FuelType;
  mileage?: number;
  technicalExpiryDate: string;
  registrationExpiryDate: string;
  kgfbExpiryDate: string;
  cascoExpiryDate?: string;
  highwayPassExpiryDate?: string;
  assignedTo?: string;
  assignedTenantId?: string;
  notes?: string;
}

// ============================================
// BACKEND → FRONTEND NORMALIZATION
// ============================================

/** Map backend UPPERCASE vehicle type to frontend lowercase */
const VEHICLE_TYPE_MAP: Record<string, RentalVehicleType> = {
  TRAILER: 'trailer',
  AGGREGATOR: 'aggregator',
  PLATFORM: 'platform',
  EQUIPMENT: 'other',
};

/** Map backend UPPERCASE status to frontend lowercase */
const RENTAL_STATUS_MAP: Record<string, RentalVehicleStatus> = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  IN_SERVICE: 'maintenance',
  SOLD: 'inactive',
  SCRAPPED: 'inactive',
};

const COMPANY_STATUS_MAP: Record<string, CompanyVehicleStatus> = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  IN_USE: 'in_use',
  IN_SERVICE: 'maintenance',
  SOLD: 'inactive',
  SCRAPPED: 'inactive',
};

/** Normalize a single backend rental vehicle to frontend format */
function normalizeRentalVehicle(raw: Record<string, unknown>): RentalVehicle {
  const vehicleType = String(raw['vehicleType'] ?? raw['vehicle_type'] ?? '');
  const status = String(raw['status'] ?? '');
  return {
    id: String(raw['id'] ?? ''),
    vehicleType: 'RENTAL',
    licensePlate: String(raw['licensePlate'] ?? raw['license_plate'] ?? ''),
    type: VEHICLE_TYPE_MAP[vehicleType] ?? 'other',
    name: String(
      raw['description'] ??
        raw['name'] ??
        (`${raw['brand'] ?? ''} ${raw['model'] ?? ''}`.trim() || 'N/A')
    ),
    brand: String(raw['brand'] ?? ''),
    model: String(raw['model'] ?? ''),
    status:
      RENTAL_STATUS_MAP[status] ?? (status.toLowerCase() as RentalVehicleStatus) ?? 'inactive',
    technicalExpiryDate: String(
      raw['technicalInspectionUntil'] ?? raw['technicalExpiryDate'] ?? ''
    ),
    registrationExpiryDate: String(
      raw['registrationValidUntil'] ?? raw['registrationExpiryDate'] ?? ''
    ),
    insuranceExpiryDate: String(raw['insuranceExpiryDate'] ?? raw['registrationValidUntil'] ?? ''),
    currentRentalId: raw['rentalEquipmentId'] ? String(raw['rentalEquipmentId']) : undefined,
    notes: raw['notes'] ? String(raw['notes']) : undefined,
    createdAt: String(raw['createdAt'] ?? raw['created_at'] ?? ''),
    updatedAt: String(raw['updatedAt'] ?? raw['updated_at'] ?? ''),
  };
}

/** Normalize a single backend company vehicle to frontend format */
function normalizeCompanyVehicle(raw: Record<string, unknown>): CompanyVehicle {
  const status = String(raw['status'] ?? '');
  const vehicleType = String(raw['vehicleType'] ?? raw['vehicle_type'] ?? '').toLowerCase();
  return {
    id: String(raw['id'] ?? ''),
    vehicleType: 'COMPANY',
    licensePlate: String(raw['licensePlate'] ?? raw['license_plate'] ?? ''),
    type: (vehicleType || 'car') as CompanyVehicleType,
    name: String(
      raw['description'] ??
        raw['name'] ??
        (`${raw['brand'] ?? ''} ${raw['model'] ?? ''}`.trim() || 'N/A')
    ),
    brand: String(raw['brand'] ?? ''),
    model: String(raw['model'] ?? ''),
    year: Number(raw['year'] ?? 0),
    status:
      COMPANY_STATUS_MAP[status] ?? (status.toLowerCase() as CompanyVehicleStatus) ?? 'inactive',
    assignedTo: raw['assignedTo'] ? String(raw['assignedTo']) : undefined,
    assignedTenantId: raw['assignedTenantId'] ? String(raw['assignedTenantId']) : undefined,
    assignedTenantName: raw['assignedTenantName'] ? String(raw['assignedTenantName']) : undefined,
    technicalExpiryDate: String(
      raw['technicalInspectionUntil'] ?? raw['technicalExpiryDate'] ?? ''
    ),
    registrationExpiryDate: String(
      raw['registrationValidUntil'] ?? raw['registrationExpiryDate'] ?? ''
    ),
    kgfbExpiryDate: String(raw['kgfbExpiryDate'] ?? raw['kgfbValidUntil'] ?? ''),
    cascoExpiryDate: raw['cascoExpiryDate'] ? String(raw['cascoExpiryDate']) : undefined,
    highwayPassExpiryDate: raw['highwayPassExpiryDate']
      ? String(raw['highwayPassExpiryDate'])
      : undefined,
    fuelType: String(raw['fuelType'] ?? 'diesel').toLowerCase() as FuelType,
    mileage: Number(raw['mileage'] ?? 0),
    notes: raw['notes'] ? String(raw['notes']) : undefined,
    createdAt: String(raw['createdAt'] ?? raw['created_at'] ?? ''),
    updatedAt: String(raw['updatedAt'] ?? raw['updated_at'] ?? ''),
  };
}

// ============================================
// API FUNCTIONS
// ============================================

/**
 * Build query string from filters
 */
function buildVehicleParams(filters: Omit<VehicleFilters, 'vehicleType'>): string {
  const params = new URLSearchParams();
  if (filters.type) params.append('type', filters.type);
  if (filters.status) params.append('status', filters.status);
  if (filters.search) params.append('search', filters.search);
  if (filters.page !== undefined) params.append('page', filters.page.toString());
  if (filters.pageSize !== undefined) params.append('pageSize', filters.pageSize.toString());
  return params.toString();
}

/**
 * Get rental vehicles only
 */
export async function getRentalVehicles(
  filters: Omit<VehicleFilters, 'vehicleType'> = {}
): Promise<VehicleListResponse<RentalVehicle>> {
  const query = buildVehicleParams(filters);
  const endpoint = query ? `/rental-vehicles?${query}` : '/rental-vehicles';
  // M-010/M-019 fix: Parse backend total from response envelope
  const raw = await api.get<{
    data: Record<string, unknown>[];
    total?: number;
    meta?: { total?: number };
  }>(endpoint);
  const data = (raw.data ?? []).map(normalizeRentalVehicle);
  const page = filters.page ?? 1;
  const pageSize = filters.pageSize ?? 20;
  const total = raw.total ?? raw.meta?.total ?? data.length;
  return {
    data,
    meta: { total, page, pageSize, hasMore: total > page * pageSize },
  };
}

/**
 * Get company vehicles only
 */
export async function getCompanyVehicles(
  filters: Omit<VehicleFilters, 'vehicleType'> = {}
): Promise<VehicleListResponse<CompanyVehicle>> {
  const query = buildVehicleParams(filters);
  const endpoint = query ? `/company-vehicles?${query}` : '/company-vehicles';
  // M-010/M-019 fix: Parse backend total from response envelope
  const raw = await api.get<{
    data: Record<string, unknown>[];
    total?: number;
    meta?: { total?: number };
  }>(endpoint);
  const data = (raw.data ?? []).map(normalizeCompanyVehicle);
  const page = filters.page ?? 1;
  const pageSize = filters.pageSize ?? 20;
  const total = raw.total ?? raw.meta?.total ?? data.length;
  return {
    data,
    meta: { total, page, pageSize, hasMore: total > page * pageSize },
  };
}

/**
 * Get single vehicle by ID (tries rental first, then company)
 */
export async function getVehicleById(id: string): Promise<VehicleResponse> {
  try {
    const raw = await api.get<{ data: Record<string, unknown> }>(`/rental-vehicles/${id}`);
    return { data: normalizeRentalVehicle(raw.data) };
  } catch {
    const raw = await api.get<{ data: Record<string, unknown> }>(`/company-vehicles/${id}`);
    return { data: normalizeCompanyVehicle(raw.data) };
  }
}

/**
 * Create new rental vehicle
 */
export async function createRentalVehicle(
  data: CreateRentalVehicleData
): Promise<VehicleResponse<RentalVehicle>> {
  return api.post<VehicleResponse<RentalVehicle>>('/rental-vehicles', data);
}

/**
 * Create new company vehicle
 */
export async function createCompanyVehicle(
  data: CreateCompanyVehicleData
): Promise<VehicleResponse<CompanyVehicle>> {
  return api.post<VehicleResponse<CompanyVehicle>>('/company-vehicles', data);
}

/**
 * Update vehicle
 * M-011 fix: Route to correct endpoint based on vehicleType
 */
export async function updateVehicle(
  id: string,
  data: Partial<CreateRentalVehicleData | CreateCompanyVehicleData>,
  vehicleType: 'RENTAL' | 'COMPANY' = 'RENTAL'
): Promise<VehicleResponse> {
  const endpoint = vehicleType === 'COMPANY' ? `/company-vehicles/${id}` : `/rental-vehicles/${id}`;
  return api.patch<VehicleResponse>(endpoint, data);
}

/**
 * Delete vehicle (soft delete)
 * M-011 fix: Route to correct endpoint based on vehicleType
 */
export async function deleteVehicle(
  id: string,
  vehicleType: 'RENTAL' | 'COMPANY' = 'RENTAL'
): Promise<{ success: boolean }> {
  const endpoint = vehicleType === 'COMPANY' ? `/company-vehicles/${id}` : `/rental-vehicles/${id}`;
  return api.delete<{ success: boolean }>(endpoint);
}
